import React, { useCallback, useState, useRef, useEffect } from 'react';
import { Block, BlockType, BulletListBlockData, ListStyle, ParagraphFragment, RowFragment, createBlock, getBlockSpacing, listStyleOf, withListStyle, SignatureStamp } from '../../config';
import { sliceHtml } from '../../utils/htmlSlice';
import { htmlToSubscriptText, subscriptTextToHtml } from '../../subscript';
import BlockRenderer from '../BlockRenderer';
import SlashCommandMenu from '../SlashCommandMenu';
import SelectionToolbar from '../SelectionToolbar';
import './block-canvas.scss';

// =======================================
// INTERFACES
// =======================================

// Engineer type for signature block
interface Engineer {
  id: string;
  first_name: string;
  last_name: string;
}

interface BlockCanvasProps {
  blocks: Block[];
  onChange: (blocks: Block[]) => void;
  isEditing: boolean;
  // Parallel to `blocks`: set where a paragraph on this page is only one piece
  // of a paragraph that continues on another page.
  fragments?: Array<ParagraphFragment | undefined>;
  /** Parallel to `blocks`: which items of an instruction list this page shows. */
  rowFragments?: Array<RowFragment | undefined>;
  onFocusPreviousPage?: () => void; // Called when backspace on first block needs to focus previous page
  onFocusBlock?: (blockId: string, atEnd?: boolean) => void; // Called to focus a block by ID (cross-page support)
  // Signature block props - passed through to BlockRenderer
  signatoryId?: string | null;
  signatoryName?: string;
  engineers?: Engineer[];
  onSignatoryChange?: (id: string) => void;
  signatureStamp?: SignatureStamp | null;
  /**
   * Blocks the template owns rather than the user. A locked block keeps its
   * handle hidden, so it cannot be moved, duplicated or deleted -- it is part
   * of the form. Defaults to nothing locked.
   */
  isBlockLocked?: (block: Block) => boolean;
  /** Restricts what "/" can insert. Passed through to SlashCommandMenu. */
  allowedBlockTypes?: BlockType[];
}

interface SlashCommandState {
  isOpen: boolean;
  position: { top: number; left: number };
  searchQuery: string;
  targetBlockIndex: number | null;
}

// Parsed block from pasted content
interface ParsedBlock {
  type: 'paragraph' | 'bullet_list' | 'heading';
  content: string;
  level?: number;
  listStyle?: ListStyle;
}

// =======================================
// MAIN COMPONENT
// =======================================

// What a fragment instance shows and how its edits merge back into the paragraph
interface FragmentContext {
  prefix: string;     // paragraph HTML before this piece
  content: string;    // this piece's HTML (latest edit)
  suffix: string;     // paragraph HTML after this piece
  sourceHtml: string; // whole-paragraph HTML the prefix/suffix were cut from
}

const BlockCanvas: React.FC<BlockCanvasProps> = ({
  blocks,
  onChange,
  isEditing,
  fragments,
  onFocusPreviousPage,
  onFocusBlock,
  signatoryId,
  signatoryName,
  engineers,
  onSignatoryChange,
  signatureStamp,
  isBlockLocked,
  allowedBlockTypes,
  rowFragments
}) => {
  const canvasRef = useRef<HTMLDivElement>(null);
  const [slashCommand, setSlashCommand] = useState<SlashCommandState>({
    isOpen: false,
    position: { top: 0, left: 0 },
    searchQuery: '',
    targetBlockIndex: null
  });
  const [focusedBlockIndex, setFocusedBlockIndex] = useState<number | null>(null);
  const [pendingFocusIndex, setPendingFocusIndex] = useState<number | null>(null);

  // Per fragment (blockId#part): the cut used to display it and to merge edits
  // back. Refreshed whenever the pagination's cut matches the current content;
  // kept as-is in the brief window between a commit and the re-measurement,
  // when the pagination's offsets are one edit behind.
  const fragmentContextsRef = useRef<Map<string, FragmentContext>>(new Map());

  const getFragmentContext = useCallback((index: number): FragmentContext | null => {
    const block = blocks[index];
    const fragment = fragments?.[index];
    if (!block || !fragment || block.type !== 'paragraph') return null;
    const whole = (block.data as { content?: string }).content || '';
    const key = `${block.id}#${fragment.part}`;
    const existing = fragmentContextsRef.current.get(key);
    if (existing && fragment.sourceHtml !== whole) {
      // Pagination not yet re-measured for the latest edit: keep the last cut
      return existing;
    }
    const ctx: FragmentContext = {
      prefix: sliceHtml(whole, 0, fragment.charStart),
      content: sliceHtml(whole, fragment.charStart, fragment.charEnd),
      suffix: sliceHtml(whole, fragment.charEnd),
      sourceHtml: whole,
    };
    fragmentContextsRef.current.set(key, ctx);
    return ctx;
  }, [blocks, fragments]);

  // Whole-paragraph HTML after replacing this fragment's piece with `pieceHtml`
  const mergeFragment = useCallback((index: number, pieceHtml: string): string | null => {
    const ctx = getFragmentContext(index);
    if (!ctx) return null;
    ctx.content = pieceHtml;
    const merged = ctx.prefix + pieceHtml + ctx.suffix;
    ctx.sourceHtml = merged;
    return merged;
  }, [getFragmentContext]);

  // Move block up in the list
  const moveBlockUp = useCallback((index: number) => {
    if (index === 0) return;
    const newBlocks = [...blocks];
    [newBlocks[index - 1], newBlocks[index]] = [newBlocks[index], newBlocks[index - 1]];
    onChange(newBlocks);
  }, [blocks, onChange]);

  // Move block down in the list
  const moveBlockDown = useCallback((index: number) => {
    if (index === blocks.length - 1) return;
    const newBlocks = [...blocks];
    [newBlocks[index], newBlocks[index + 1]] = [newBlocks[index + 1], newBlocks[index]];
    onChange(newBlocks);
  }, [blocks, onChange]);

  // Delete a block
  const deleteBlock = useCallback((index: number) => {
    const newBlocks = blocks.filter((_, i) => i !== index);
    onChange(newBlocks);
  }, [blocks, onChange]);

  // Update a block's data. For a paragraph fragment the incoming content is
  // only this page's piece; merge it into the whole paragraph.
  const updateBlock = useCallback((index: number, updatedBlock: Block) => {
    const newBlocks = [...blocks];
    if (fragments?.[index] && updatedBlock.type === 'paragraph') {
      const piece = (updatedBlock.data as { content?: string }).content || '';
      const merged = mergeFragment(index, piece);
      if (merged !== null) {
        newBlocks[index] = { ...updatedBlock, data: { ...updatedBlock.data, content: merged } as Block['data'] };
        onChange(newBlocks);
        return;
      }
    }
    newBlocks[index] = updatedBlock;
    onChange(newBlocks);
  }, [blocks, onChange, fragments, mergeFragment]);

  // Toggle the blank line above or below a block
  const toggleSpacing = useCallback((index: number, side: 'above' | 'below') => {
    const block = blocks[index];
    if (!block) return;
    const current = getBlockSpacing(block);
    const newBlocks = [...blocks];
    newBlocks[index] = {
      ...block,
      spacing: { ...current, [side]: !current[side] }
    };
    onChange(newBlocks);
  }, [blocks, onChange]);

  // Duplicate a block
  const duplicateBlock = useCallback((index: number) => {
    const blockToCopy = blocks[index];
    const newBlock: Block = {
      ...blockToCopy,
      id: crypto.randomUUID()
    };
    const newBlocks = [...blocks];
    newBlocks.splice(index + 1, 0, newBlock);
    onChange(newBlocks);
  }, [blocks, onChange]);

  // Replace a block with a new type, preserving content where applicable
  const replaceBlock = useCallback((index: number, type: BlockType, headingLevel?: number, listStyle?: ListStyle) => {
    const currentBlock = blocks[index];
    if (!currentBlock) return;

    // A list into another kind of list keeps its items exactly as they are;
    // only the markers change.
    if (currentBlock.type === 'bullet_list' && type === 'bullet_list') {
      const newBlocks = [...blocks];
      newBlocks[index] = { ...currentBlock, data: withListStyle(currentBlock.data as BulletListBlockData, listStyle ?? 'bullet') };
      onChange(newBlocks);
      return;
    }
    
    const newBlock = createBlock(type);
    
    // Extract text content from current block
    let textContent = '';
    switch (currentBlock.type) {
      case 'paragraph':
        textContent = (currentBlock.data as { content: string }).content || '';
        break;
      case 'heading':
        textContent = (currentBlock.data as { text: string }).text || '';
        break;
      case 'bullet_list':
        // Join all items
        textContent = ((currentBlock.data as { items: string[] }).items || []).join('\n');
        break;
      case 'scope_list':
        // Join all item texts
        textContent = ((currentBlock.data as { items: { text: string }[] }).items || [])
          .map(item => item.text)
          .join('\n');
        break;
    }
    
    // Strip HTML tags and decode HTML entities for plain text. A paragraph's
    // <sub> becomes the plain-text fields' `_{...}` (subscript.ts), so a
    // subscript survives being turned into a heading or a list.
    let plainText = htmlToSubscriptText(textContent).trim();
    
    // Don't preserve slash command text (e.g., "/bul", "/head")
    if (plainText.startsWith('/')) {
      plainText = '';
    }
    
    // Apply content to new block based on type
    switch (type) {
      case 'paragraph':
        (newBlock.data as { content: string }).content = currentBlock.type === 'paragraph'
          ? textContent
          : subscriptTextToHtml(textContent);
        break;
      case 'heading':
        (newBlock.data as { text: string; level: number }).text = plainText;
        if (headingLevel) {
          (newBlock.data as { level: number }).level = headingLevel;
        }
        break;
      case 'bullet_list':
        // Split by newlines or use as single item
        const bulletItems = plainText.split('\n').filter(item => item.trim());
        newBlock.data = withListStyle(
          { ...(newBlock.data as BulletListBlockData), items: bulletItems.length > 0 ? bulletItems : [''] },
          listStyle ?? 'bullet'
        );
        break;
      case 'scope_list':
        // Split by newlines or use as single item
        const scopeItems = plainText.split('\n').filter(item => item.trim());
        (newBlock.data as { items: { id: string; text: string; indent: number }[] }).items = 
          scopeItems.length > 0 
            ? scopeItems.map(text => ({ id: crypto.randomUUID(), text, indent: 0 }))
            : [{ id: crypto.randomUUID(), text: '', indent: 0 }];
        break;
    }
    
    const newBlocks = [...blocks];
    newBlocks[index] = newBlock;
    onChange(newBlocks);
  }, [blocks, onChange]);

  // Check if a block is empty
  const isBlockEmpty = useCallback((block: Block): boolean => {
    if (block.type === 'paragraph') {
      const content = (block.data as { content?: string }).content || '';
      // Consider empty if no content, just whitespace, or just <br>
      return !content.trim() || content.trim() === '<br>';
    }
    // For other block types, consider them not empty (they have structure)
    return false;
  }, []);

  // Effect to focus a block when pendingFocusIndex is set
  useEffect(() => {
    if (pendingFocusIndex === null) return;
    
    // Small delay to ensure DOM is ready
    const timer = setTimeout(() => {
      const blockWrappers = canvasRef.current?.querySelectorAll('.block-wrapper');
      const targetBlock = blockWrappers?.[pendingFocusIndex];
      const editableElement = targetBlock?.querySelector('[contenteditable="true"]') as HTMLElement;
      
      if (editableElement) {
        editableElement.focus();
        // Place cursor at start for empty blocks
        const range = document.createRange();
        const sel = window.getSelection();
        range.selectNodeContents(editableElement);
        range.collapse(true);
        sel?.removeAllRanges();
        sel?.addRange(range);
      }
      
      setPendingFocusIndex(null);
    }, 50);
    
    return () => clearTimeout(timer);
  }, [pendingFocusIndex, blocks.length]); // Re-run when blocks.length changes

  // Handle clicking the empty area to add a new paragraph
  const handleEmptyAreaClick = useCallback(() => {
    if (!isEditing) return;
    
    // Check if the last block is an empty paragraph - if so, just focus it
    const lastBlock = blocks[blocks.length - 1];
    if (lastBlock && isBlockEmpty(lastBlock)) {
      // Focus the existing empty block
      setPendingFocusIndex(blocks.length - 1);
      return;
    }
    
    // Create a new paragraph block
    const newBlock = createBlock('paragraph');
    onChange([...blocks, newBlock]);
    
    // Set pending focus to the new block (will be at index blocks.length)
    setPendingFocusIndex(blocks.length);
  }, [isEditing, blocks, onChange, isBlockEmpty]);

  // Handle slash command opening
  const handleSlashCommand = useCallback((index: number, position: { top: number; left: number }) => {
    setSlashCommand({
      isOpen: true,
      position,
      searchQuery: '',
      targetBlockIndex: index
    });
  }, []);

  // Handle slash command search update
  const handleSlashSearch = useCallback((query: string) => {
    setSlashCommand(prev => ({
      ...prev,
      searchQuery: query
    }));
  }, []);

  // Handle slash command selection
  const handleSlashSelect = useCallback((type: BlockType, variant?: { headingLevel?: number; listStyle?: ListStyle }) => {
    const targetIndex = slashCommand.targetBlockIndex;
    if (targetIndex !== null) {
      replaceBlock(targetIndex, type, variant?.headingLevel, variant?.listStyle);
      
      // Focus the new block after React re-renders
      setTimeout(() => {
        const blockWrappers = canvasRef.current?.querySelectorAll('.block-wrapper');
        const targetBlock = blockWrappers?.[targetIndex];
        const editableElement = targetBlock?.querySelector('[contenteditable="true"]') as HTMLElement;
        if (editableElement) {
          editableElement.focus();
        }
      }, 50);
    }
    setSlashCommand({
      isOpen: false,
      position: { top: 0, left: 0 },
      searchQuery: '',
      targetBlockIndex: null
    });
  }, [slashCommand.targetBlockIndex, replaceBlock]);

  // Handle slash command close
  const handleSlashClose = useCallback(() => {
    setSlashCommand({
      isOpen: false,
      position: { top: 0, left: 0 },
      searchQuery: '',
      targetBlockIndex: null
    });
  }, []);

  // Handle block focus
  const handleBlockFocus = useCallback((index: number) => {
    setFocusedBlockIndex(index);
  }, []);

  // Handle block blur
  const handleBlockBlur = useCallback(() => {
    // Delay to allow toolbar/slash command clicks to register
    setTimeout(() => {
      // Check if there's still a text selection (toolbar might be in use)
      const selection = window.getSelection();
      const hasSelection = selection && !selection.isCollapsed;
      
      if (!slashCommand.isOpen && !hasSelection) {
        setFocusedBlockIndex(null);
      }
    }, 150);
  }, [slashCommand.isOpen]);

  // Handle Enter key in a block to create new paragraph below.
  // currentContent is saved atomically with the new block creation; afterContent
  // (text to the right of the caret, paragraphs only) seeds the new block so
  // Enter splits a paragraph like any other editor. An empty paragraph simply
  // gets another empty paragraph below it (a blank line), as in Word/Notion.
  const handleBlockEnter = useCallback((index: number, currentContent?: string, afterContent?: string) => {
    const newBlock = createBlock('paragraph');
    const newBlocks = [...blocks];
    const currentBlock = newBlocks[index];

    // Enter inside a fragment splits the WHOLE paragraph: the part before the
    // caret (plus any earlier pieces) stays; the rest (plus later pieces on
    // following pages) becomes the new paragraph.
    const ctx = fragments?.[index] && currentBlock?.type === 'paragraph' ? getFragmentContext(index) : null;
    if (ctx && currentContent !== undefined) {
      const wholeBefore = ctx.prefix + currentContent;
      const wholeAfter = (afterContent || '') + ctx.suffix;
      ctx.content = currentContent;
      ctx.suffix = '';
      ctx.sourceHtml = wholeBefore;
      (newBlock.data as { content: string }).content = wholeAfter;
      newBlocks[index] = { ...currentBlock, data: { ...currentBlock.data, content: wholeBefore } as Block['data'] };
      newBlocks.splice(index + 1, 0, newBlock);
      onChange(newBlocks);
      if (onFocusBlock) {
        onFocusBlock(newBlock.id, false);
      }
      return;
    }

    if (afterContent) {
      (newBlock.data as { content: string }).content = afterContent;
    }

    // Update current block content and create new paragraph
    if (currentContent !== undefined && currentBlock) {
      const updatedData = { ...currentBlock.data };
      
      // Set the appropriate property based on block type
      switch (currentBlock.type) {
        case 'paragraph':
          (updatedData as { content: string }).content = currentContent;
          break;
        case 'heading':
          (updatedData as { text: string }).text = currentContent;
          break;
        // List blocks handle Enter internally, but just in case:
        default:
          // Try to set content as a fallback
          if ('content' in updatedData) {
            (updatedData as { content: string }).content = currentContent;
          }
      }
      
      newBlocks[index] = {
        ...currentBlock,
        data: updatedData
      };
    }
    
    // Then add the new block
    newBlocks.splice(index + 1, 0, newBlock);
    onChange(newBlocks);

    // Focus the new block after render - use cross-page handler if available
    if (onFocusBlock) {
      // Let DocumentEditor handle focus (works across pages). No timer here:
      // it waits for the block to render itself, and a delay is visible as
      // the caret sitting in the old block before it moves down.
      onFocusBlock(newBlock.id, false);
    } else {
      // Fallback: try to focus within this canvas
      setTimeout(() => {
        const blockElements = canvasRef.current?.querySelectorAll('.block-wrapper');
        const newBlockElement = blockElements?.[index + 1]?.querySelector(
          '[contenteditable="true"]'
        ) as HTMLElement;
        newBlockElement?.focus();
      }, 50);
    }
  }, [blocks, onChange, onFocusBlock, fragments, getFragmentContext]);

  // Handle Backspace on empty block to delete it
  const handleBlockBackspace = useCallback((index: number) => {
    if (blocks.length <= 1) return; // Keep at least one block
    
    const newBlocks = blocks.filter((_, i) => i !== index);
    onChange(newBlocks);

    // If deleting the first block, need to focus previous page's last block
    if (index === 0 && onFocusPreviousPage) {
      onFocusPreviousPage();
      return;
    }

    // Focus the nearest editable block above on this page (skipping spacers,
    // dividers and images, which have nothing to focus)
    setTimeout(() => {
      const blockElements = canvasRef.current?.querySelectorAll('.block-wrapper');
      let prevBlockElement: HTMLElement | null = null;
      for (let i = index - 1; i >= 0 && blockElements; i--) {
        const editables = blockElements[i]?.querySelectorAll('[contenteditable="true"]');
        if (editables && editables.length > 0) {
          prevBlockElement = editables[editables.length - 1] as HTMLElement;
          break;
        }
      }
      if (prevBlockElement) {
        prevBlockElement.focus();
        // Place cursor at end
        const range = document.createRange();
        const sel = window.getSelection();
        range.selectNodeContents(prevBlockElement);
        range.collapse(false); // false = collapse to end
        sel?.removeAllRanges();
        sel?.addRange(range);
      }
    }, 50);
  }, [blocks, onChange, onFocusPreviousPage]);

  /**
   * Leave a bullet list, the way a word processor does: Enter on the trailing
   * empty bullet, or Backspace on the last one, drops that bullet and puts the
   * caret in ordinary text below.
   *
   * The list passes back the items it keeps rather than saving them itself.
   * Two updates -- one to trim the list, one to add the paragraph -- would both
   * be built from the same `blocks`, and the second would discard the first.
   */
  const handleExitList = useCallback((index: number, remainingItems: string[]) => {
    const paragraph = createBlock('paragraph');
    const newBlocks = [...blocks];
    const list = newBlocks[index];

    if (remainingItems.length === 0) {
      // Nothing left of the list; the paragraph takes its place, so the caret
      // stays on the line the user was already on.
      newBlocks.splice(index, 1, paragraph);
    } else {
      newBlocks[index] = {
        ...list,
        data: { ...list.data, items: remainingItems } as Block['data'],
      };
      newBlocks.splice(index + 1, 0, paragraph);
    }
    onChange(newBlocks);

    if (onFocusBlock) {
      onFocusBlock(paragraph.id, false);
      return;
    }
    setTimeout(() => {
      const blockElements = canvasRef.current?.querySelectorAll('.block-wrapper');
      const target = remainingItems.length === 0 ? index : index + 1;
      const editable = blockElements?.[target]?.querySelector(
        '[contenteditable="true"]'
      ) as HTMLElement | undefined;
      editable?.focus();
    }, 50);
  }, [blocks, onChange, onFocusBlock]);

  // Handle paste that creates multiple blocks (e.g., pasted bullet list).
  // The pasted blocks go AFTER the current block; the current block keeps its
  // text (saved here from `currentContent` in the same update). Only an empty
  // paragraph is replaced outright.
  const handlePasteMultiBlock = useCallback((index: number, parsedBlocks: ParsedBlock[], currentContent?: string) => {
    if (parsedBlocks.length === 0) return;
    
    const newBlocks: Block[] = [];
    
    for (const parsed of parsedBlocks) {
      if (parsed.type === 'bullet_list') {
        // Create a bullet list block with items
        const items = parsed.content.split('\n').filter(item => item.trim());
        const bulletBlock = createBlock('bullet_list');
        bulletBlock.data = withListStyle({ ...(bulletBlock.data as BulletListBlockData), items }, parsed.listStyle ?? 'bullet');
        newBlocks.push(bulletBlock);
      } else if (parsed.type === 'paragraph') {
        // Create a paragraph block
        const paragraphBlock = createBlock('paragraph');
        (paragraphBlock.data as { content: string }).content = parsed.content;
        newBlocks.push(paragraphBlock);
      } else if (parsed.type === 'heading') {
        // Create a heading block
        const headingBlock = createBlock('heading');
        (headingBlock.data as { text: string; level: number }).text = parsed.content;
        (headingBlock.data as { text: string; level: number }).level = parsed.level || 2;
        newBlocks.push(headingBlock);
      }
    }
    
    if (newBlocks.length === 0) return;

    const updatedBlocks = [...blocks];
    const currentBlock = updatedBlocks[index];
    // Inside a fragment the current content is only this page's piece
    if (fragments?.[index] && currentBlock?.type === 'paragraph' && currentContent !== undefined) {
      currentContent = mergeFragment(index, currentContent) ?? currentContent;
    }
    const currentIsEmptyParagraph = currentBlock?.type === 'paragraph' &&
      (!currentContent || !currentContent.replace(/<br\s*\/?>/gi, '').trim());

    let lastNewIndex: number;
    if (currentIsEmptyParagraph) {
      // Nothing to keep - the pasted blocks take the empty paragraph's place
      updatedBlocks.splice(index, 1, ...newBlocks);
      lastNewIndex = index + newBlocks.length - 1;
    } else {
      // Keep the current block (with its latest text) and insert below it
      if (currentBlock?.type === 'paragraph' && currentContent !== undefined) {
        updatedBlocks[index] = {
          ...currentBlock,
          data: { ...currentBlock.data, content: currentContent } as Block['data']
        };
      }
      updatedBlocks.splice(index + 1, 0, ...newBlocks);
      lastNewIndex = index + newBlocks.length;
    }
    onChange(updatedBlocks);

    // Focus the last new block
    setTimeout(() => {
      const blockElements = canvasRef.current?.querySelectorAll('.block-wrapper');
      const lastBlockElement = blockElements?.[lastNewIndex]?.querySelector(
        '[contenteditable="true"]'
      ) as HTMLElement;
      lastBlockElement?.focus();
    }, 50);
  }, [blocks, onChange, fragments, mergeFragment]);

  return (
    <div ref={canvasRef} className={`block-canvas ${isEditing ? 'editing' : 'readonly'}`}>
      {blocks.map((block, index) => {
        // A blank line sits between two blocks only when both sides want it
        const spacing = getBlockSpacing(block);
        const previous = index > 0 ? blocks[index - 1] : null;
        const hasGapAbove = !!previous && spacing.above && getBlockSpacing(previous).below;
        // A paragraph fragment shows only its piece of the paragraph
        const fragment = fragments?.[index];
        const fragmentCtx = fragment ? getFragmentContext(index) : null;
        const displayBlock: Block = fragmentCtx
          ? { ...block, data: { ...block.data, content: fragmentCtx.content } as Block['data'] }
          : block;
        return (
        <div
          key={block.id}
          data-block-id={block.id}
          data-fragment-part={fragment ? fragment.part : undefined}
          className={[
            'block-wrapper',
            focusedBlockIndex === index ? 'focused' : '',
            hasGapAbove ? 'has-gap-above' : '',
            fragment ? 'is-fragment' : '',
            fragment && fragment.part > 0 ? 'continues-from-previous' : '',
            fragment && fragment.part < fragment.parts - 1 ? 'continues-on-next' : ''
          ].filter(Boolean).join(' ')}
        >
          {isEditing && !isBlockLocked?.(block) && (
            <div className="block-handle">
              <button 
                className="handle-btn" 
                title="Drag to move · Click for options"
              >
                <svg width="14" height="14" viewBox="0 0 14 14" fill="currentColor">
                  <circle cx="4" cy="3" r="1.5"/>
                  <circle cx="10" cy="3" r="1.5"/>
                  <circle cx="4" cy="7" r="1.5"/>
                  <circle cx="10" cy="7" r="1.5"/>
                  <circle cx="4" cy="11" r="1.5"/>
                  <circle cx="10" cy="11" r="1.5"/>
                </svg>
              </button>
              <div className="block-menu">
                <button 
                  className="menu-item" 
                  onClick={() => moveBlockUp(index)}
                  disabled={index === 0}
                >
                  <span className="icon">↑</span>
                  <span>Move up</span>
                </button>
                <button 
                  className="menu-item" 
                  onClick={() => moveBlockDown(index)}
                  disabled={index === blocks.length - 1}
                >
                  <span className="icon">↓</span>
                  <span>Move down</span>
                </button>
                <button
                  className="menu-item"
                  onClick={() => duplicateBlock(index)}
                >
                  <span className="icon">⧉</span>
                  <span>Duplicate</span>
                </button>
                <div className="menu-divider"></div>
                <button 
                  className="menu-item danger" 
                  onClick={() => deleteBlock(index)}
                >
                  <span className="icon">×</span>
                  <span>Delete</span>
                </button>
              </div>
            </div>
          )}
          <div className="block-content">
            <BlockRenderer
              block={displayBlock}
              fragment={fragment}
              rowFragment={rowFragments?.[index]}
              onChange={(updatedBlock) => updateBlock(index, updatedBlock)}
              onDelete={() => deleteBlock(index)}
              isEditing={isEditing}
              onSlashCommand={(position) => handleSlashCommand(index, position)}
              onSlashSearch={handleSlashSearch}
              onSlashClose={handleSlashClose}
              slashSearchQuery={slashCommand.targetBlockIndex === index ? slashCommand.searchQuery : ''}
              isSlashMenuOpen={slashCommand.isOpen && slashCommand.targetBlockIndex === index}
              onFocus={() => handleBlockFocus(index)}
              onBlur={handleBlockBlur}
              onEnter={(content, afterContent) => handleBlockEnter(index, content, afterContent)}
              onBackspaceEmpty={() => handleBlockBackspace(index)}
              onExitList={(remainingItems) => handleExitList(index, remainingItems)}
              onPasteMultiBlock={(parsedBlocks, currentContent) => handlePasteMultiBlock(index, parsedBlocks, currentContent)}
              signatoryId={signatoryId}
              signatoryName={signatoryName}
              engineers={engineers}
              onSignatoryChange={onSignatoryChange}
              signatureStamp={signatureStamp}
            />
          </div>
        </div>
        );
      })}

      {/* Invisible click area to add new block - Notion style */}
      {isEditing && (
        <div 
          className="canvas-click-area"
          onClick={handleEmptyAreaClick}
        />
      )}

      {/* Slash Command Menu */}
      <SlashCommandMenu
        isOpen={slashCommand.isOpen}
        onClose={handleSlashClose}
        onSelect={handleSlashSelect}
        position={slashCommand.position}
        searchQuery={slashCommand.searchQuery}
        allowedBlockTypes={allowedBlockTypes}
      />

      {/* Selection Toolbar - appears when text is selected */}
      <SelectionToolbar
        canvasRef={canvasRef}
        isEditing={isEditing}
        getBlockInfo={(index) => {
          const block = blocks[index];
          if (!block) return null;
          return {
            type: block.type,
            headingLevel: block.type === 'heading'
              ? (block.data as { level?: 1 | 2 | 3 })?.level
              : undefined,
            listStyle: block.type === 'bullet_list'
              ? listStyleOf(block.data as BulletListBlockData)
              : undefined,
            spacing: getBlockSpacing(block)
          };
        }}
        onBlockTypeChange={(type, level, blockIndex, listStyle) => {
          if (blockIndex !== undefined) {
            replaceBlock(blockIndex, type, level, listStyle);
          }
        }}
        onSpacingToggle={(side, blockIndex) => toggleSpacing(blockIndex, side)}
      />
    </div>
  );
};

export default BlockCanvas;
